import styled, { css } from "styled-components";
import { FC, PropsWithChildren } from "react";

import Text from "@/components/Text";
import { ReactComponent as TrendingUp } from "@/assets/icons/trending-up.svg";
import { ReactComponent as TrendingDown } from "@/assets/icons/trending-down.svg";
import PerformanceCardTeam from "./PerformanceCardTeam";
import { useSelector } from "react-redux";
import { RootState } from "@/utils/store";

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 8px 26px rgba(34, 34, 34, 0.05);
  border-radius: 4px;
  padding: 16px;
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Heading = styled(Text)`
  margin-bottom: 5px;
  span {
    font-weight: 400;
  }
`;

const IconHolder = styled.div`
  background: linear-gradient(180deg, #f2f3f7 0%, #e6e9f4 100%);
  border-radius: 8px;
  height: 40px;
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 12px;
`;

const Percentage = styled(Text)`
  margin-left: 4px;
`;

const TrendingHolder = styled.div`
  margin-left: 8px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  svg {
    width: 7px;
  }
`;

const SubtitleHolder = styled.div`
  display: flex;
  align-items: center;
`;

const Subtitle = styled(Text)`
  max-width: 226px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Teams = styled.div`
  display: flex;
  margin-left: 12px;
  > div,
  img {
    position: relative;
    &:nth-child(1) {
      transform: translateX(-4px);
      z-index: 4;
    }
    &:nth-child(2) {
      transform: translateX(-8px);
      z-index: 3;
    }
    &:nth-child(3) {
      transform: translateX(-12px);
      z-index: 2;
    }
    &:nth-child(4) {
      transform: translateX(-16px);
      z-index: 1;
    }
  }
`;

export const TeamCount = styled.div`
  ${({ theme }) => css`
    font-family: ${theme.typography.button.fontFamily};
  `}
  border: 1px solid ${({ theme }) => theme.colors.white};
  width: 20px;
  height: 20px;
  background: #f8f8f8;
  border-radius: 50%;
  font-size: 8px;
  line-height: 12px;
  font-weight: 700;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface Props {
  value: number | string;
  title: string;
  subtitle: string;
  date?: string;
  trendingPercent?: number;
  teamIds?: string;
  isTeamsCount?: boolean;
}

const PerformanceCard: FC<PropsWithChildren<Props>> = ({
  value,
  title,
  subtitle,
  date,
  children,
  trendingPercent,
  teamIds,
  isTeamsCount,
}) => {
  const teamIdsArray = teamIds?.split(",");

  const teams = useSelector((state: RootState) => state.teams);

  const userTeamIds = Object.keys(teams).filter((id) => !isNaN(Number(id)));

  const userCompetitionTeamsIds = teamIdsArray?.filter((team) =>
    userTeamIds.includes(team)
  );

  const first3Teams = userCompetitionTeamsIds?.slice(0, 3);

  const teamsCount = userCompetitionTeamsIds?.length;

  const renderTeamsSubtitle = () => {
    if (!teamsCount) return "-";
    if (userTeamIds?.length === userCompetitionTeamsIds.length)
      return "All Teams";
    if (teamsCount === 1) return teams[first3Teams![0]].name;
    return `${teamsCount} Teams`;
  };

  return (
    <Card>
      <CardTop>
        <div>
          <Heading $type="smallBold">
            {title}{" "}
            {!!date && (
              <>
                · <span>{date}</span>{" "}
              </>
            )}
          </Heading>
          <SubtitleHolder>
            {!!isTeamsCount ? (
              <Subtitle $type="smallBold" $color="gray">
                {renderTeamsSubtitle()}
              </Subtitle>
            ) : (
              <Subtitle $type="smallBold" $color="gray">
                {subtitle}
              </Subtitle>
            )}
            {!!first3Teams?.length && (
              <Teams>
                {!!teamsCount && teamsCount > 3 && (
                  <TeamCount>+{teamsCount - 3}</TeamCount>
                )}
                {first3Teams.map((teamId) => (
                  <PerformanceCardTeam key={teamId} teamId={teamId} />
                ))}
              </Teams>
            )}
            {!!trendingPercent && (
              <TrendingHolder>
                {trendingPercent > 0 && <TrendingUp />}
                {trendingPercent < 0 && <TrendingDown />}
                <Percentage
                  $type="extraSmall"
                  $color={trendingPercent > 0 ? "success" : "error"}
                >
                  {trendingPercent} %
                </Percentage>
              </TrendingHolder>
            )}
          </SubtitleHolder>
        </div>
        <div>
          <IconHolder>{children}</IconHolder>
        </div>
      </CardTop>
      {!!isTeamsCount ? (
        <Text $type="heading1">{!!teamsCount ? teamsCount : 0}</Text>
      ) : (
        <Text $type="heading1">{value}</Text>
      )}
    </Card>
  );
};

export default PerformanceCard;
